import { Atom } from "@effect-atom/atom";
import { useAtomSet, useAtomValue } from "@effect-atom/atom-react";
import { Effect, Option } from "effect";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import React from "react";
import { Button } from "~/components/ui/button.tsx";
import { AppStoreConfigLive, loadAppStoreConfig } from "~/config/app-store.ts";
import type { AppStoreConfig } from "~/config/app-store.ts";

/**
 * Atom to load app store config
 */
const configAtom = Atom.make<AppStoreConfig | null>(null);

/**
 * Atom to track if user has clicked the waitlist CTA in nav
 */
const showFormAtom = Atom.make(false);

/**
 * Atom to track initialization status
 */
const navConfigInitializedAtom = Atom.make(false);

export const Navigation: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const headerOpacity = useTransform(scrollYProgress, [0, 0.1], [0, 1]);
  const headerY = useTransform(scrollYProgress, [0, 0.1], [-20, 0]);

  const config = useAtomValue(configAtom);
  const setConfig = useAtomSet(configAtom);
  const showForm = useAtomValue(showFormAtom);
  const setShowForm = useAtomSet(showFormAtom);
  const navConfigInitialized = useAtomValue(navConfigInitializedAtom);
  const setNavConfigInitialized = useAtomSet(navConfigInitializedAtom);

  // Load config on first render
  if (!navConfigInitialized) {
    setNavConfigInitialized(true);
    Effect.runPromise(
      loadAppStoreConfig.pipe(
        Effect.provide(AppStoreConfigLive),
        Effect.tap((cfg) => Effect.sync(() => setConfig(cfg))),
        Effect.catchAll((error) =>
          Effect.sync(() => {
            console.error("Failed to load app store config:", error);
            setConfig({ mode: "waitlist", iosUrl: Option.none(), androidUrl: Option.none() });
          })
        ),
      ),
    );
  }

  const renderCTA = () => {
    if (!config) return null;

    if (config.mode === "waitlist") {
      return (
        <AnimatePresence mode="wait">
          {!showForm ?
            (
              <motion.div
                key="nav-cta"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Button
                  onClick={() => setShowForm(true)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(220,20,60,0.4)] rounded-full px-6 transition-all hover:scale-105"
                >
                  Join Waitlist
                </Button>
              </motion.div>
            ) :
            (
              <motion.div
                key="nav-form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-2"
              >
                <input
                  type="email"
                  placeholder="Enter email"
                  className="h-10 px-4 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-zinc-400 text-sm focus:outline-none focus:border-primary w-48"
                />
                <Button
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4"
                >
                  Join
                </Button>
              </motion.div>
            )}
        </AnimatePresence>
      );
    }

    // Live mode: Link to app store (placeholder - could link to iOS by default)
    const url = Option.getOrElse(config.iosUrl, () => "#");
    return (
      <Button
        asChild
        className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(220,20,60,0.4)] rounded-full px-6 transition-all hover:scale-105"
      >
        <a href={url} target="_blank" rel="noopener noreferrer">
          Download App
        </a>
      </Button>
    );
  };

  return (
    <motion.nav
      style={{ opacity: headerOpacity, y: headerY }}
      className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4 bg-zinc-950/80 backdrop-blur-md border-b border-white/5"
    >
      <div className="text-sm font-heading font-bold tracking-widest uppercase text-white">
        FLIRTMACHINE
      </div>
      <div className="flex items-center gap-6">
        {renderCTA()}
      </div>
    </motion.nav>
  );
};
